import { Mot } from '../mots/mot';
import { Line } from '../line';
import { TimetableTrafficNode } from '../trafficNodes/timetableTrafficNode';
import { Platform } from '../platform';
import { Disruption } from '../disruptions/disruption';
import { MetaData } from '../MetaData';
import { isNullOrUndefined, isUndefined } from 'util';
import { Location } from '../location';
/**
 * class {TimetableEvent}
 * should be used to display timetableEvent related information
 */
export class TimetableEvent extends MetaData {
    /**
     * constructor of class
     * please use factory instead
     * @see {this.factory)
     * @param id
     * @param mot
     * @param line
     * @param platform
     * @param scheduledArrivalDateTime
     * @param scheduledDepartureDateTime
     * @param actualArrivalDateTime
     * @param actualDepartureDateTime
     * @param nodes
     * @param disruptions List of Disruption objects to hold information on
     * @param additionalInfo List of additionalInfo objects to hold information on (e.g. disability-friendly Transport)
     * @param sessionId of the last Timetable request
     * @param requestId of the last Timetable request
     * @param realTimeControlled Boolean of status of real time control of the line
     * @param pathOnMap locations of path on map
     */
    constructor(id, mot, line, platform, scheduledArrivalDateTime, scheduledDepartureDateTime, actualArrivalDateTime, actualDepartureDateTime, nodes, disruptions, additionalInfo, sessionId, requestId, realTimeControlled, pathOnMap, metaData) {
        super(metaData);
        this._id = id;
        this._mot = mot;
        this._line = line;
        this._platform = platform;
        this._scheduledArrivalDateTime = scheduledArrivalDateTime;
        this._scheduledDepartureDateTime = scheduledDepartureDateTime;
        this._actualArrivalDateTime = actualArrivalDateTime;
        this._actualDepartureDateTime = actualDepartureDateTime;
        this._nodes = nodes;
        this._disruptions = disruptions;
        this._additionalInfo = additionalInfo;
        this._sessionId = sessionId;
        this._requestId = requestId;
        this._realTimeControlled = realTimeControlled;
        this._pathOnMap = pathOnMap;
    }
    /**
     * Factory method to create an instance of this class without
     * having to correctly fill the constructor.
     * !!! You should always use this instead of the constructor !!!
     * @param values
     * @returns {TimetableEvent}
     */
    static factory(values) {
        const sanitizedValues = {};
        sanitizedValues.id = values.id || '';
        sanitizedValues.mot = values.mot ? Mot.factory(values.mot) : Mot.factory({});
        sanitizedValues.line = values.line ? Line.factory(values.line) : Line.factory({});
        sanitizedValues.platform = values.platform ? Platform.factory(values.platform) : Line.factory({});
        sanitizedValues.scheduledArrivalDateTime = values.scheduledArrivalDateTime || undefined;
        sanitizedValues.scheduledDepartureDateTime = values.scheduledDepartureDateTime || undefined;
        sanitizedValues.actualArrivalDateTime = values.actualArrivalDateTime || undefined;
        sanitizedValues.actualDepartureDateTime = values.actualDepartureDateTime || undefined;
        sanitizedValues.sessionId = values.sessionId || '';
        sanitizedValues.requestId = values.requestId || '';
        sanitizedValues.nodes = [];
        if (values.nodes) {
            for (const node of values.nodes) {
                sanitizedValues.nodes.push(TimetableTrafficNode.factory(node));
            }
        }
        sanitizedValues.disruptions = [];
        if (values.disruptions) {
            for (const disruption of values.disruptions) {
                sanitizedValues.disruptions.push(Disruption.factory(disruption));
            }
        }
        sanitizedValues.additionalInfo = values.additionalInfo ? values.additionalInfo : [];
        sanitizedValues.metaData = !isUndefined(values.metaData) ? values.metaData : {};
        sanitizedValues.realTimeControlled = !isNullOrUndefined(values.realTimeControlled) ? values.realTimeControlled : false;
        sanitizedValues.pathOnMap = [];
        if (values.pathOnMap) {
            for (const location of values.pathOnMap) {
                sanitizedValues.pathOnMap.push(Location.factory(location));
            }
        }
        return new TimetableEvent(sanitizedValues.id, sanitizedValues.mot, sanitizedValues.line, sanitizedValues.platform, sanitizedValues.scheduledArrivalDateTime, sanitizedValues.scheduledDepartureDateTime, sanitizedValues.actualArrivalDateTime, sanitizedValues.actualDepartureDateTime, sanitizedValues.nodes, sanitizedValues.disruptions, sanitizedValues.additionalInfo, sanitizedValues.sessionId, sanitizedValues.requestId, sanitizedValues.realTimeControlled, sanitizedValues.pathOnMap, sanitizedValues.metaData);
    }
    /**
     * gets the internal _id
     * @returns {string}
     */
    get id() {
        return this._id;
    }
    /**
     * sets the internal _id
     * @param value
     */
    set id(value) {
        this._id = value;
    }
    /**
     * gets all trafficNodes for this timetableEvent
     * @returns {TimetableTrafficNode[]}
     */
    get nodes() {
        return this._nodes;
    }
    /**
     * sets all trafficNodes for this timetableEvent
     * @param value
     */
    set nodes(value) {
        this._nodes = value;
    }
    /**
     * gets the real departure date-time
     * @returns {Date}
     */
    get actualDepartureDateTime() {
        return this._actualDepartureDateTime;
    }
    /**
     * sets the real departure date-time
     * @param value
     */
    set actualDepartureDateTime(value) {
        this._actualDepartureDateTime = value;
    }
    /**
     * gets the real arrival date-time
     * @returns {Date}
     */
    get actualArrivalDateTime() {
        return this._actualArrivalDateTime;
    }
    /**
     * sets the real arrival date-time
     * @param value
     */
    set actualArrivalDateTime(value) {
        this._actualArrivalDateTime = value;
    }
    /**
     * gets the scheduled departure date-time
     * @returns {Date}
     */
    get scheduledDepartureDateTime() {
        return this._scheduledDepartureDateTime;
    }
    /**
     * sets the scheduled departure date-time
     * @param value
     */
    set scheduledDepartureDateTime(value) {
        this._scheduledDepartureDateTime = value;
    }
    /**
     * gets the scheduled arrival date-time
     * @returns {Date}
     */
    get scheduledArrivalDateTime() {
        return this._scheduledArrivalDateTime;
    }
    /**
     * sets the scheduled arrival date-time
     * @param value
     */
    set scheduledArrivalDateTime(value) {
        this._scheduledArrivalDateTime = value;
    }
    /**
     * gets the line for this timetableEvent
     * @returns {Line}
     */
    get line() {
        return this._line;
    }
    /**
     * sets the line for this timetableEvent
     * @param value
     */
    set line(value) {
        this._line = value;
    }
    /**
     * gets the mot used for this timetableEvent
     * @returns {Mot}
     */
    get platform() {
        return this._platform;
    }
    /**
     * sets the platform for this timetableEvent
     * @param value
     */
    set platform(value) {
        this._platform = value;
    }
    /**
     * gets the mot used for this timetableEvent
     * @returns {Mot}
     */
    get mot() {
        return this._mot;
    }
    /**
     * gets the disruption information for this partialConnection
     * @returns {Disruption[]}
     */
    get disruptions() {
        return this._disruptions;
    }
    /**
     * sets the disruption information for this partialConnection
     * @param value
     */
    set disruptions(value) {
        this._disruptions = value;
    }
    /**
     * gets the array of additional info strings from the api
     * @returns {string[]}
     */
    get additionalInfo() {
        return this._additionalInfo;
    }
    /**
     * sets the array of additional info strings from the api
     * @param value
     */
    set additionalInfo(value) {
        this._additionalInfo = value;
    }
    /**
     * gets the sessionId for the current connection
     * @returns {string}
     */
    get sessionId() {
        return this._sessionId;
    }
    /**
     * sets the sessionId for the current connection
     * @param value
     */
    set sessionId(value) {
        this._sessionId = value;
    }
    /**
     * gets the requestId for the current connection
     * @returns {string}
     */
    get requestId() {
        return this._requestId;
    }
    /**
     * sets the requestId for the current connection
     * @param value
     */
    set requestId(value) {
        this._requestId = value;
    }
    /**
     * gets the boolean of whether the connection is real time controlled
     * @returns {boolean} true if realtime delays are available
     */
    get realTimeControlled() {
        return this._realTimeControlled;
    }
    /**
     * sets the boolean of whether the connection is real time controlled
     * @param value boolean
     */
    set realTimeControlled(value) {
        this._realTimeControlled = value;
    }
    /**
     * gets the list of locations for the path render on a map
     * @returns Location array
     */
    get pathOnMap() {
        return this._pathOnMap;
    }
    /**
     * sets the list of locations for the path render on a map
     * @param value Location[]
     */
    set pathOnMap(value) {
        this._pathOnMap = value;
    }
}
//# sourceMappingURL=timetableEvent.js.map